import { useCallback, useEffect, useRef, useState } from 'react';
import { priceOnFinish } from '../supplementary_components/priceInputDelegate';
import { useBoardStore } from './boardStore';
import { useShallow } from 'zustand/react/shallow';
import { calculateTruckWeeklyRpm } from './plannerBoardSettings';
import styles from './mainBoardSettings.module.scss';

function buildAtHomeTruckListKey(trucksAtHome, trucksById) {
    const numbers = [...trucksAtHome]
        .map((id) => trucksById[id]?.truckNumber)
        .filter(Boolean)
        .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
    return numbers.length > 0 ? numbers.join('\n') : '';
}

function isTruckGrossVisible(truckId, grossVisibleTruckIds) {
    if (grossVisibleTruckIds == null) return true;
    return grossVisibleTruckIds.has(String(truckId));
}

const AtHomeStat = ({ count, truckListKey }) => {
    const [listOpen, setListOpen] = useState(false);
    const anchorRef = useRef(null);
    const truckNumbers = truckListKey ? truckListKey.split('\n') : [];
    const countClickable = count > 0 && truckNumbers.length > 0;

    const toggleList = useCallback((e) => {
        e.stopPropagation();
        setListOpen((open) => !open);
    }, []);

    useEffect(() => {
        if (!listOpen) return;
        const onDocMouseDown = (e) => {
            if (anchorRef.current && !anchorRef.current.contains(e.target)) {
                setListOpen(false);
            }
        };
        const onKeyDown = (e) => {
            if (e.key === 'Escape') setListOpen(false);
        };
        document.addEventListener('mousedown', onDocMouseDown);
        document.addEventListener('keydown', onKeyDown);
        return () => {
            document.removeEventListener('mousedown', onDocMouseDown);
            document.removeEventListener('keydown', onKeyDown);
        };
    }, [listOpen]);

    return (
        <span className={styles.atHomeStat} ref={anchorRef}>
            {countClickable ? (
                <button
                    type="button"
                    className={`${styles.atHomeStatBtn} ${listOpen ? styles.atHomeStatBtnOpen : ''}`}
                    onClick={toggleList}
                    aria-label="Show trucks at home"
                    aria-expanded={listOpen}
                    aria-haspopup="true"
                >
                    {`At Home: ${count}`}
                </button>
            ) : (
                `At Home: ${count}`
            )}
            {listOpen && (
                <span className={styles.atHomeListPopover} role="region" aria-label="Trucks at home">
                    {truckNumbers.map((truckNumber) => (
                        <span key={truckNumber} className={styles.atHomeListLine}>
                            {truckNumber}
                        </span>
                    ))}
                </span>
            )}
        </span>
    );
};

export const MainBoardStatistics = ({userCanSeeLoadRates}) => {
    const boardStatistics = useBoardStore((s) => s.boardStatistics);
    const stats = useBoardStore(
        useShallow((s) => {
            const visibleGrossEntries = Object.entries(s.trucksTotalGross).filter(([truckId]) =>
                isTruckGrossVisible(truckId, s.grossVisibleTruckIds)
            );
            const totalGross = userCanSeeLoadRates
                ? visibleGrossEntries.reduce((sum, [, gross]) => sum + (gross || 0), 0)
                : 0;
            const averageGross =
                userCanSeeLoadRates && visibleGrossEntries.length > 0
                    ? totalGross / visibleGrossEntries.length
                    : null;

            let averageRpm = null;
            if (userCanSeeLoadRates) {
                let rpmSum = 0;
                let rpmCount = 0;
                for (const [truckId] of visibleGrossEntries) {
                    const truck = s.trucksById[truckId];
                    const rpm = calculateTruckWeeklyRpm(truck?.thisWeeksLoads);
                    if (rpm == null) continue;
                    rpmSum += rpm;
                    rpmCount += 1;
                }
                if (rpmCount > 0) averageRpm = rpmSum / rpmCount;
            }

            return {
                atHomeCount: s.trucksAtHome.size,
                atHomeTruckListKey: buildAtHomeTruckListKey(s.trucksAtHome, s.trucksById),
                activeCount: Object.keys(s.trucksById).length - s.trucksAtHome.size,
                openTrucksCount: s.openTrucks.size,
                totalGross,
                averageGross,
                averageRpm,
            };
        })
    );

    const show = boardStatistics || {};

    return <div className={styles.mainBoardStatistics}>
        {show.activeTrucks !== false ? <span>{`Active: ${stats.activeCount}`}</span> : null}
        {show.openTrucks !== false ? (
            <span>{`Open: `}<span className={stats.openTrucksCount > 0 ? styles.open : styles.notOpen}>{stats.openTrucksCount}</span></span>
        ) : null}
        {show.atHomeTrucks !== false ? (
            <AtHomeStat count={stats.atHomeCount} truckListKey={stats.atHomeTruckListKey} />
        ) : null}
        {userCanSeeLoadRates && show.totalGross !== false ? (
            <span>{`Total Gross: ${priceOnFinish(stats.totalGross, true)}`}</span>
        ) : null}
        {userCanSeeLoadRates && show.averageGross ? (
            <span>{`Avg Gross: ${stats.averageGross == null ? '-' : priceOnFinish(stats.averageGross, true)}`}</span>
        ) : null}
        {userCanSeeLoadRates && show.averageRpm ? (
            <span>{`Avg RPM: ${stats.averageRpm == null ? '-' : ('$' + stats.averageRpm.toFixed(2))}`}</span>
        ) : null}
    </div>
}
